/**
 * Lightweight bus for call-related ephemeral events from the sync store.
 *
 * Call state is intentionally outside the durable event log, so it does not
 * belong in IndexedDB. Components subscribe here instead.
 */

import type { EventOrigin } from '@/api/socket'
import type { CallInvitedEvent, CallUpdatedEvent, UUID } from '@/api/types'

export type CallEphemeralEvent = CallInvitedEvent | CallUpdatedEvent

type Listener = (event: CallEphemeralEvent) => void

const listeners = new Set<Listener>()

export function subscribeCallEvents(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The same `call.updated` goes out on the room's topic and on every
 * participant's own (server `broadcast_wire`), and a tab in the meeting holds
 * both. Each topic keeps its own order; the two do not keep one between them,
 * so the room's copy of the newest update can land before the user topic's
 * copy of the one before it. The call carries no version to tell them apart.
 *
 * What does tell them apart: every update reaches a topic once. A copy whose
 * content matches one already applied from the *other* topic, and not yet
 * seen on this one, is that update's second copy — applying it again would
 * at best do nothing and at worst take the call back to how it was. A copy
 * that matches one this topic has already delivered is a new update that
 * happens to look like an old one, and is applied.
 */
interface AppliedCopy {
  content: string
  topics: Set<EventOrigin>
}

const COPIES_PER_CALL = 16
const CALLS_REMEMBERED = 32
const applied = new Map<UUID, AppliedCopy[]>()

function isSecondCopy(event: CallUpdatedEvent, origin: EventOrigin): boolean {
  const call = event.payload.call
  const content = JSON.stringify(call)
  const copies = applied.get(call.id) ?? []

  // The earliest copy not yet seen here: each topic delivers in order.
  const first = copies.find((copy) => copy.content === content && !copy.topics.has(origin))
  if (first !== undefined) {
    first.topics.add(origin)
    return true
  }

  copies.push({ content, topics: new Set([origin]) })
  if (copies.length > COPIES_PER_CALL) copies.shift()
  // Most recently updated last, so the call forgotten first is the oldest.
  applied.delete(call.id)
  applied.set(call.id, copies)
  if (applied.size > CALLS_REMEMBERED) {
    const oldest = applied.keys().next().value
    if (oldest !== undefined) applied.delete(oldest)
  }
  return false
}

/**
 * `origin` is the topic the event came in on. Without one, nothing is
 * treated as a second copy.
 */
export function emitCallEvent(event: CallEphemeralEvent, origin?: EventOrigin): void {
  if (event.type === 'call.updated' && origin !== undefined && isSecondCopy(event, origin)) return
  for (const listener of listeners) listener(event)
}
